import type { FeatureFlags, StaffRole } from '@ucp/web/lib';

/**
 * Trips roles per capability (PRO-0710, ADR-018 PRO-0710 amendment). Mirrors the server role →
 * capability map in `apps/api/src/auth/capabilities.ts`. UX only: the server re-checks every
 * call, and holding two stage roles never lets one person act at two stages of one trip.
 */
export const TRIP_CREATE_ROLES: readonly StaffRole[] = ['TRIP_LEADER', 'PRINCIPAL'];
export const TRIP_REVIEW_ROLES: readonly StaffRole[] = ['TRIP_EVC'];
export const TRIP_APPROVE_ROLES: readonly StaffRole[] = ['PRINCIPAL'];
export const TRIP_APPROVE_HIGH_RISK_ROLES: readonly StaffRole[] = ['OPS_DIRECTOR'];
export const TRIP_MODE_PROPOSE_ROLES: readonly StaffRole[] = ['TRIP_EVC'];
export const TRIP_MODE_CONFIRM_ROLES: readonly StaffRole[] = ['PRINCIPAL'];
/** The review queue is caller-scoped: one of these roles at any provision opens it. */
export const TRIP_OVERSIGHT_ROLES: readonly StaffRole[] = ['TRIP_EVC', 'PRINCIPAL', 'OPS_DIRECTOR'];

/** Every role with a trips capability; the `/trips/*` RouteGuard. Never L6 (ADR-018 EoP). */
export const TRIPS_ROUTE_ROLES: readonly StaffRole[] = [
  ...new Set<StaffRole>([
    ...TRIP_CREATE_ROLES,
    ...TRIP_REVIEW_ROLES,
    ...TRIP_APPROVE_ROLES,
    ...TRIP_APPROVE_HIGH_RISK_ROLES,
    ...TRIP_MODE_PROPOSE_ROLES,
    ...TRIP_MODE_CONFIRM_ROLES,
    ...TRIP_OVERSIGHT_ROLES,
  ]),
];

/**
 * `trip_calendar_read` (Increment 1.2 Phase B): summaries in the register and calendar only.
 * Admitted to the module only while `tripsSchoolCalendarReadEnabled` is on (TD-10).
 */
export const TRIP_CALENDAR_READ_ROLES: readonly StaffRole[] = [
  'TRIP_LEADER',
  'SCHOOL_BUSINESS_MANAGER',
];

/**
 * The group-wide activity library role (PRO-3220 Phase 2, ADR-018 PRO-3220 Phase 2 amendment).
 * Admitted to the module only while `tripsActivityLibraryEnabled` is on; it opens the Activity
 * library tab and nothing else.
 */
export const ACTIVITY_LIBRARY_GROUP_ROLES: readonly StaffRole[] = ['ACTIVITY_LIBRARY_LEAD'];

/**
 * School roles that open the library at the active school: any `trip_create`, `trip_review`,
 * `trip_approve` or `trip_approve_high_risk` holder (TIP TD-11).
 */
export const ACTIVITY_LIBRARY_SCHOOL_ROLES: readonly StaffRole[] = [
  ...new Set<StaffRole>([
    ...TRIP_CREATE_ROLES,
    ...TRIP_REVIEW_ROLES,
    ...TRIP_APPROVE_ROLES,
    ...TRIP_APPROVE_HIGH_RISK_ROLES,
  ]),
];

/** The `/trips/*` RouteGuard and chrome role set for these flags (TD-10). */
export function tripsRouteRoles(
  flags:
    | Partial<Pick<FeatureFlags, 'tripsSchoolCalendarReadEnabled' | 'tripsActivityLibraryEnabled'>>
    | null
    | undefined,
): readonly StaffRole[] {
  const calendar = flags?.tripsSchoolCalendarReadEnabled === true;
  const library = flags?.tripsActivityLibraryEnabled === true;
  if (!calendar && !library) return TRIPS_ROUTE_ROLES;
  return [
    ...new Set<StaffRole>([
      ...TRIPS_ROUTE_ROLES,
      ...(calendar ? TRIP_CALENDAR_READ_ROLES : []),
      ...(library ? ACTIVITY_LIBRARY_GROUP_ROLES : []),
    ]),
  ];
}
